import LoadingAnimation from "@/components/LoadingAnimation";
import { useDropZone, useFileUpload, useUploader } from "./hooks";
import { Button } from "../../../components/ui/Button";
import { Icon } from "../../../components/ui/Icon";
import { ChangeEvent, useCallback } from "react";
import { FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import { useThemeContextSelector } from "@/utils/theme";

export const ImageUploader = ({ onUpload }: { onUpload: (url: string) => void }) => {
    const isDarkMode = useThemeContextSelector(state => state.isDarkMode);
    const { isLoading, uploadFile } = useUploader({ onUpload });
    const { handleUploadClick, ref } = useFileUpload();
    const { draggedInside, onDrop, onDragEnter, onDragLeave } = useDropZone({ uploader: uploadFile });

    const onFileChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => (e.target.files ? uploadFile(e.target.files[0]) : null),
        [uploadFile]
    );

    if (isLoading) {
        return (
            <FlexRowAlignCenter
                style={{
                    justifyContent: "center",
                    padding: "32px",
                    borderRadius: "8px",
                    height: "200px",
                }}>
                <LoadingAnimation />
            </FlexRowAlignCenter>
        );
    }

    const wrapperStyle = {
        alignItems: "center",
        justifyContent: "center",
        paddingInline: "32px",
        paddingBlock: "40px",
        borderRadius: "8px",
        height: "200px",
        ...(draggedInside && {
            backgroundColor: "#F5F5F5",
        }),
    };

    return (
        <FlexColumn
            style={wrapperStyle}
            onDrop={onDrop}
            onDragOver={onDragEnter}
            onDragLeave={onDragLeave}
            contentEditable={false}>
            <Icon
                name="Image"
                style={{
                    width: "48px",
                    height: "48px",
                    marginBottom: "16px",
                    color: isDarkMode ? "#FFFFFF" : "#000000",
                    opacity: 0.2,
                }}
            />
            <FlexColumnAlignJustifyCenter style={{ gap: "8px" }}>
                <div
                    style={{
                        fontSize: "14px",
                        fontWeight: 500,
                        textAlign: "center",
                        color: isDarkMode ? "#737373" : "#A3A3A3",
                    }}
                    data-testid="auto-id-d9760eb9-229a-4a24-8a50-d0dfdb9f4ae7">
                    {draggedInside ? "Drop image here" : "Drag and drop or"}
                </div>
                <div data-testid="auto-id-cb09fe78-8874-4d42-8377-2abb82baceb6">
                    <Button
                        disabled={draggedInside}
                        onClick={handleUploadClick}
                        variant="primary"
                        buttonSize="small"
                        data-testid="auto-id-e2aea8f6-a870-46a9-838d-c08191e19795">
                        <Icon name="Upload" />
                        Upload an image
                    </Button>
                </div>
            </FlexColumnAlignJustifyCenter>
            <input
                style={{ width: 0, height: 0, overflow: "hidden", opacity: 0 }}
                ref={ref}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.gif"
                onChange={onFileChange}
                data-testid="auto-id-92f3048d-7e08-4f0a-a5ee-2a14a69cb0e8" />
        </FlexColumn>
    );
};

export default ImageUploader;
